import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import { useTheme } from "@/app/providers/ThemeContext";
import { FONT_MONO, FONT_SERIF } from "@/shared/lib/typography";

gsap.registerPlugin(ScrollTrigger);

/* ── Data ─────────────────────────────────────────────────────────────────── */

interface Tool {
  label: string;
  tip: string;
}

interface Category {
  name: string;
  tools: Tool[];
}

const categories: Category[] = [
  {
    name: "Options Pricing & Greeks",
    tools: [
      { label: "Greeks", tip: "Real-time delta, gamma, theta, vega, rho" },
      { label: "Probability", tip: "Probability of profit with breakeven analysis" },
      { label: "Expected Move", tip: "Implied move from option IV" },
      { label: "Live Price", tip: "Live bid/ask/mid, volume, open interest" },
      { label: "Buying Power", tip: "Margin requirements and collateral" },
      { label: "Greek Sensitivity", tip: "Per-leg Greek sensitivity analysis" },
    ],
  },
  {
    name: "Volatility & Structure",
    tools: [
      { label: "IV Percentile", tip: "IV vs historical realized vol distribution" },
      { label: "IV Surface", tip: "Full IV surface and term structure" },
      { label: "Skew & Smile", tip: "Skew pattern detection across strikes" },
      { label: "IV History", tip: "Historical IV time series" },
      { label: "IV vs RV Spread", tip: "Implied vs realized spread analysis" },
    ],
  },
  {
    name: "Flow & Positioning",
    tools: [
      { label: "Put/Call Ratio", tip: "P/C ratios by open interest and volume" },
      { label: "OI Heatmap", tip: "Open interest & volume across the chain" },
      { label: "Institutional Flow", tip: "Large block trade flow analysis" },
    ],
  },
  {
    name: "Risk & Execution",
    tools: [
      { label: "Portfolio Risk", tip: "Aggregated Greeks and Value-at-Risk" },
      { label: "Scenario Stress", tip: "Stress test positions under scenarios" },
      { label: "Strategy Scanner", tip: "Multi-leg strategy optimization" },
      { label: "Vol Surface Arb", tip: "Volatility arbitrage detection" },
    ],
  },
  {
    name: "Stock Data & Technicals",
    tools: [
      { label: "Quote", tip: "Real-time price, volume, 52-week range" },
      { label: "RSI", tip: "Relative Strength Index momentum" },
      { label: "MACD", tip: "Moving Average Convergence Divergence" },
      { label: "VWAP", tip: "Volume Weighted Average Price" },
      { label: "Bollinger", tip: "Bollinger Bands volatility channels" },
      { label: "ATR", tip: "Average True Range volatility" },
      { label: "Stochastic", tip: "Stochastic oscillator %K/%D" },
      { label: "ADX", tip: "Trend strength indicator" },
      { label: "Pivot Points", tip: "Floor trader support/resistance levels" },
      { label: "Relative Vol", tip: "Volume vs historical average" },
      { label: "Earnings", tip: "EPS, surprise data, calendar" },
      { label: "Analyst Ratings", tip: "Consensus rating and trends" },
    ],
  },
  {
    name: "Macro & Context",
    tools: [
      { label: "Economic Calendar", tip: "FOMC, CPI, NFP release dates" },
      { label: "Macro Regime", tip: "VIX, yields, SPY regime snapshot" },
      { label: "Catalyst Snapshot", tip: "Earnings proximity + headlines" },
      { label: "FRED Data", tip: "Federal Reserve economic indicators" },
      { label: "Sector Performance", tip: "Sector ETF ranking vs SPY" },
      { label: "Dividends", tip: "Dividend history and calendar" },
    ],
  },
];

const oracleTools: Tool[] = [
  { label: "Full Analysis", tip: "Complete verdict, thesis, and execution plan" },
  { label: "Watch Creation", tip: "Set up condition-based monitoring alerts" },
  { label: "Scenario Engine", tip: "Multi-scenario stress testing" },
  { label: "Conformance", tip: "Output validation and quality check" },
  { label: "Strategy Optimizer", tip: "Scan and rank multi-leg strategies" },
];

/* ── Prompt data with tool-highlight mapping ──────────────────────────────── */

interface PromptData {
  text: string;
  highlights: string[];
}

const prompts: PromptData[] = [
  {
    text: "Should I buy AAPL 200C for next month?",
    highlights: ["Greeks", "Probability", "Expected Move", "Live Price", "IV Percentile", "Full Analysis"],
  },
  {
    text: "Where is skew dislocated right now?",
    highlights: ["Skew & Smile", "IV Surface", "IV vs RV Spread", "IV History"],
  },
  {
    text: "Alert me when IV rank drops below 60",
    highlights: ["IV Percentile", "Watch Creation", "Put/Call Ratio"],
  },
  {
    text: "Compare this to similar setups in the last year",
    highlights: ["Quote", "RSI", "MACD", "Earnings", "Scenario Stress", "Analyst Ratings"],
  },
];

/* ── Build a Set of all highlighted labels for a given prompt index ────── */

function getHighlightSet(activePrompt: number | null): Set<string> {
  if (activePrompt === null) return new Set();
  return new Set(prompts[activePrompt].highlights);
}

/* ── Pill component with tooltip + external highlight state ───────────── */

function ToolPill({
  tool,
  oracle,
  highlighted,
}: {
  tool: Tool;
  oracle?: boolean;
  highlighted: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const isLit = hovered || highlighted;

  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        display: "inline-block",
        fontFamily: FONT_MONO,
        fontSize: "0.62rem",
        padding: "8px 10px",
        borderRadius: "4px",
        cursor: "default",
        transition: "all 0.2s ease",
        color: isLit 
          ? (isDark ? "rgba(255,255,255,0.9)" : "#0088a3") 
          : (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.6)"),
        background: isLit
          ? "rgba(0,184,219,0.08)"
          : oracle
            ? (isDark ? "rgba(0,184,219,0.03)" : "rgba(0,184,219,0.05)")
            : (isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)"),
        border: isLit
          ? "1px solid rgba(0,184,219,0.4)"
          : oracle
            ? (isDark ? "1px solid rgba(0,184,219,0.15)" : "1px solid rgba(0,184,219,0.25)")
            : (isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.1)"),
      }}
    >
      {tool.label}

      {/* Tooltip */}
      <span
        style={{
          position: "absolute",
          bottom: "calc(100% + 8px)",
          left: "50%",
          transform: "translateX(-50%)",
          background: isDark ? "#151c24" : "#f8f9fa",
          color: isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.8)",
          fontFamily: FONT_MONO,
          fontSize: "0.58rem",
          lineHeight: 1.4,
          padding: "6px 10px",
          borderRadius: "4px",
          maxWidth: "220px",
          whiteSpace: "normal",
          textAlign: "center",
          pointerEvents: "none",
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.1s ease",
          zIndex: 50,
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        }}
      >
        {tool.tip}
      </span>
    </span>
  );
}

/* ── Main component ───────────────────────────────────────────────────────── */

export default function ToolsShowcase() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const h2Ref = useRef<HTMLHeadingElement>(null);
  const promptsRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<(HTMLDivElement | null)[]>([]);
  const oracleRef = useRef<HTMLDivElement>(null);
  const claudeRef = useRef<HTMLDivElement>(null);

  const [activePrompt, setActivePrompt] = useState<number | null>(null);
  const highlightSet = getHighlightSet(activePrompt);

  useEffect(() => {
    const ctx = gsap.context(() => {
      /* ── Header stagger reveal ──────────────────────────────────────── */
      const headerTl = gsap.timeline({
        scrollTrigger: {
          trigger: headerRef.current,
          start: "top 82%",
          toggleActions: "play none none none",
        },
      });

      headerTl
        .fromTo(
          badgeRef.current,
          { opacity: 0, y: 18, scale: 0.92 },
          { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "power3.out" },
        )
        .fromTo(
          h2Ref.current,
          { opacity: 0, y: 28 },
          { opacity: 1, y: 0, duration: 0.65, ease: "power3.out" },
          "-=0.3",
        )
        ;

      /* ── Prompt grid stagger in ─────────────────────────────────────── */
      if (promptsRef.current) {
        const promptEls = promptsRef.current.querySelectorAll(".prompt-item");
        gsap.fromTo(
          promptEls,
          { opacity: 0, y: 14 },
          {
            opacity: 1,
            y: 0,
            duration: 0.45,
            stagger: 0.08,
            ease: "power2.out",
            scrollTrigger: {
              trigger: promptsRef.current,
              start: "top 88%",
              toggleActions: "play none none none",
            },
          },
        );
      }

      /* ── Category batches stagger in ───────────────────────────────── */
      categoryRefs.current.forEach((el) => {
        if (!el) return;
        const pills = el.querySelectorAll(".tool-pill");
        const header = el.querySelector(".cat-header");

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: el,
            start: "top 95%",
            toggleActions: "play none none none",
          },
        });

        if (header) {
          tl.fromTo(
            header,
            { opacity: 0, y: 10 },
            { opacity: 1, y: 0, duration: 0.35, ease: "power2.out" },
          );
        }

        if (pills.length) {
          tl.fromTo(
            pills,
            { opacity: 0, y: 12, scale: 0.92 },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 0.35,
              stagger: 0.04,
              ease: "power2.out",
            },
            "-=0.15",
          );
        }
      });

      /* ── Oracle row ─────────────────────────────────────────────────── */
      if (oracleRef.current) {
        const oraclePills = oracleRef.current.querySelectorAll(".tool-pill");
        const oracleHeader = oracleRef.current.querySelector(".cat-header");

        const oracleTl = gsap.timeline({
          scrollTrigger: {
            trigger: oracleRef.current,
            start: "top 95%",
            toggleActions: "play none none none",
          },
        });

        if (oracleHeader) {
          oracleTl.fromTo(
            oracleHeader,
            { opacity: 0, y: 10 },
            { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" },
          );
        }

        if (oraclePills.length) {
          oracleTl.fromTo(
            oraclePills,
            { opacity: 0, y: 12, scale: 0.92 },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 0.35,
              stagger: 0.06,
              ease: "power2.out",
            },
            "-=0.15",
          );
        }
      }

      /* ── Claude icon fade in ────────────────────────────────────────── */
      if (claudeRef.current) {
        gsap.fromTo(
          claudeRef.current,
          { opacity: 0, y: 16, scale: 0.9 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.5,
            ease: "power2.out",
            scrollTrigger: {
              trigger: claudeRef.current,
              start: "top 90%",
              toggleActions: "play none none none",
            },
          },
        );
      }
    }, sectionRef);

    // Safety net: ensure all elements become visible even if ScrollTrigger misses
    const safetyTimer = setTimeout(() => {
      const pills = sectionRef.current?.querySelectorAll('.tool-pill, .cat-header, .prompt-item');
      pills?.forEach(el => {
        (el as HTMLElement).style.opacity = '1';
        (el as HTMLElement).style.transform = 'none';
      });
      // Also ensure claude icon is visible
      if (claudeRef.current) {
        claudeRef.current.style.opacity = '1';
        claudeRef.current.style.transform = 'none';
      }
    }, 2000);

    return () => {
      ctx.revert();
      clearTimeout(safetyTimer);
    };
  }, []);

  return (
    <div
      id="tools"
      ref={sectionRef}
      style={{ background: isDark ? "var(--lh-bg-1)" : "#f7f7f5", position: "relative", zIndex: 1 }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "72px 24px" }}>
        {/* ── Header ────────────────────────────────────────────────────── */}
        <div ref={headerRef} style={{ textAlign: "center", marginBottom: "48px" }}>
          <div
            ref={badgeRef}
            className="inline-flex items-center justify-center gap-2 mb-4"
            style={{
              fontFamily: FONT_MONO,
              fontSize: "0.55rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: isDark ? "rgba(255,255,255,0.50)" : "rgba(0,0,0,0.50)",
              border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.12)",
              borderRadius: "100px",
              padding: "6px 14px",
              opacity: 0,
            }}
          >
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: isDark ? "var(--lh-cyan)" : "#00B8DB",
                display: "inline-block",
              }}
            />
            Platform Capabilities
          </div>

          <h2
            ref={h2Ref}
            style={{
              fontFamily: FONT_SERIF,
              fontSize: "clamp(1.4rem, 2.8vw, 2.2rem)",
              fontWeight: 700,
              color: isDark ? "white" : "#111111",
              lineHeight: 1.25,
              letterSpacing: "-0.01em",
              maxWidth: "800px",
              margin: "0 auto",
              opacity: 0,
            }}
          >
            61 tools.{" "}
            <span style={{ color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)" }}>
              From options chain analytics to macro regime signals — everything professional traders need to research, decide, and act.
            </span>
          </h2>
        </div>

        {/* ── Prompt questions (2x2 grid, MOVED TO TOP) ───────────────── */}
        <div
          ref={promptsRef}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "16px 32px",
            marginBottom: "48px",
            paddingBottom: "36px",
            borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.08)",
          }}
          className="prompts-grid"
        >
          {prompts.map((prompt, idx) => (
            <div
              key={prompt.text}
              className="prompt-item"
              style={{
                fontFamily: FONT_MONO,
                fontSize: "0.95rem",
                color:
                  activePrompt === idx
                    ? (isDark ? "rgba(255,255,255,1)" : "#111111")
                    : (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
                lineHeight: 1.55,
                cursor: "default",
                transition: "color 0.2s ease",
                opacity: 0,
                padding: "8px 0",
              }}
              onMouseEnter={() => setActivePrompt(idx)}
              onMouseLeave={() => setActivePrompt(null)}
            >
              <span style={{ color: "#00B8DB", marginRight: "8px" }}>&gt;</span>
              {`"${prompt.text}"`}
            </div>
          ))}
        </div>

        {/* ── Tool pill cloud (3-col grid) ──────────────────────────────── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "28px",
          }}
          className="tools-grid"
        >
          {categories.map((cat, ci) => (
            <div
              key={cat.name}
              ref={(el) => {
                categoryRefs.current[ci] = el;
              }}
            >
              <div
                className="cat-header"
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: "0.5rem",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: isDark ? "rgba(255,255,255,0.50)" : "rgba(0,0,0,0.50)",
                  marginBottom: "8px",
                  opacity: 0,
                }}
              >
                {cat.name}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                {cat.tools.map((tool) => (
                  <span key={tool.label} className="tool-pill" style={{ opacity: 0 }}>
                    <ToolPill
                      tool={tool}
                      highlighted={highlightSet.has(tool.label)}
                    />
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ── Oracle Intelligence row ───────────────────────────────────── */}
        <div
          ref={oracleRef}
          style={{
            marginTop: "36px",
            paddingTop: "28px",
            borderTop: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              background: isDark ? "rgba(0,184,219,0.02)" : "rgba(0,184,219,0.04)",
              borderRadius: "8px",
              padding: "20px 24px",
              textAlign: "center",
            }}
          >
            <div
              className="cat-header"
              style={{
                fontFamily: FONT_MONO,
                fontSize: "0.5rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#00B8DB",
                marginBottom: "8px",
                opacity: 0,
              }}
            >
              Oracle Intelligence
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "5px",
                justifyContent: "center",
              }}
            >
              {oracleTools.map((tool) => (
                <span key={tool.label} className="tool-pill" style={{ opacity: 0 }}>
                  <ToolPill
                    tool={tool}
                    oracle
                    highlighted={highlightSet.has(tool.label)}
                  />
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Claude icon at bottom ─────────────────────────────────────── */}
        <div
          ref={claudeRef}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: "48px",
            opacity: 0,
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: "#D4A574",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "box-shadow 0.3s ease, transform 0.3s ease",
              boxShadow:
                activePrompt !== null
                  ? "0 0 30px rgba(212,165,116,0.3)"
                  : "0 0 0px rgba(212,165,116,0)",
              transform:
                activePrompt !== null ? "scale(1.05)" : "scale(1)",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {Array.from({ length: 12 }).map((_, i) => {
                const angle = i * 30;
                const rad = (angle * Math.PI) / 180;
                const x1 = 12 + Math.cos(rad) * 4;
                const y1 = 12 + Math.sin(rad) * 4;
                const x2 = 12 + Math.cos(rad) * 10;
                const y2 = 12 + Math.sin(rad) * 10;
                return (
                  <line
                    key={i}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="#FDF0E2"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                );
              })}
            </svg>
          </div>
          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: "0.52rem",
              letterSpacing: "0.08em",
              color: isDark ? "rgba(255,255,255,0.50)" : "rgba(0,0,0,0.5)",
              marginTop: "10px",
            }}
          >
            Powered by Claude
          </span>
        </div>
      </div>

      {/* ── Responsive styles ───────────────────────────────────────────── */}
      <style>{`
        @media (max-width: 1024px) {
          .tools-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .prompts-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 640px) {
          .tools-grid {
            grid-template-columns: 1fr !important;
          }
          .prompts-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
